import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  configureTask,
  createTask,
  deleteTask,
  finalizeQuote,
  getSnapshot,
  getStateHistory,
  getTask,
  getTaskApplications,
  writeTaskApplications,
  listAllAssignedTasks,
  listAllMyTasks,
  listAllTasks,
  listMyTasks,
  listTasks,
  respondAndCloseTask,
  updateTask,
  writeSnapshot,
  type ConfigureRequest,
  type CreateTaskInput,
  type FinalizeQuoteRequest,
  type FinalizeQuoteResponse,
  type ListTasksQuery,
  type PaginatedTaskStateHistory,
  type PaginatedTasks,
  type RespondAndCloseTaskResponse,
  type SnapshotApplicationInput,
  type TaskApplicationLink,
  type TaskDetail,
  type TaskSnapshot,
  type TaskSnapshotInput,
  type UpdateTaskInput,
} from "@/api/tasks";
import { useWorkFor } from "@/hooks/useWorkFor";

export const TASKS_KEY = ["tasks"] as const;

export function useMyTasks(query: ListTasksQuery = {}, enabled = true) {
  const workFor = useWorkFor();
  const identity =
    workFor.kind === "self"
      ? "self"
      : workFor.kind === "registered"
        ? `registered:${workFor.userId}`
        : `other:${workFor.email}`;
  return useQuery<PaginatedTasks>({
    queryKey: [...TASKS_KEY, "mine", identity, query],
    queryFn: () => listMyTasks(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useTasksQueue(query: ListTasksQuery = {}, enabled = true) {
  return useQuery<PaginatedTasks>({
    queryKey: [...TASKS_KEY, "queue", query],
    queryFn: () => listTasks(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

// Fetch-all variants (issue #8): return every row (no page cap) by looping the
// paged endpoint until exhausted. Used by the Tasks modal so the list shows the
// full history rather than stopping at a fixed page size.
export function useAllMyTasks(query: ListTasksQuery = {}, enabled = true) {
  const workFor = useWorkFor();
  const identity =
    workFor.kind === "self"
      ? "self"
      : workFor.kind === "registered"
        ? `registered:${workFor.userId}`
        : `other:${workFor.email}`;
  return useQuery<PaginatedTasks>({
    queryKey: [...TASKS_KEY, "mine", "all", identity, query],
    queryFn: () => listAllMyTasks(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAllAssignedTasks(
  query: ListTasksQuery = {},
  enabled = true,
) {
  return useQuery<PaginatedTasks>({
    queryKey: [...TASKS_KEY, "assigned", "all", query],
    queryFn: () => listAllAssignedTasks(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAllTasksQueue(query: ListTasksQuery = {}, enabled = true) {
  return useQuery<PaginatedTasks>({
    queryKey: [...TASKS_KEY, "queue", "all", query],
    queryFn: () => listAllTasks(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useTask(taskId: string | null | undefined, enabled = true) {
  return useQuery<TaskDetail>({
    queryKey: [...TASKS_KEY, "detail", taskId],
    queryFn: () => getTask(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useTaskApplications(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<TaskApplicationLink[]>({
    queryKey: [...TASKS_KEY, "applications", taskId],
    queryFn: () => getTaskApplications(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useWriteTaskApplications() {
  const qc = useQueryClient();
  return useMutation<
    TaskApplicationLink[],
    unknown,
    { taskId: string; applications: SnapshotApplicationInput[] }
  >({
    mutationFn: ({ taskId, applications }) =>
      writeTaskApplications(taskId, applications),
    onSuccess: (_data, { taskId }) => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "applications", taskId] });
    },
  });
}

export function useTaskSnapshot(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<TaskSnapshot | null>({
    queryKey: [...TASKS_KEY, "snapshot", taskId],
    queryFn: () => getSnapshot(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useTaskStateHistory(
  taskId: string | null | undefined,
  query: { limit?: number; offset?: number } = {},
  enabled = true,
) {
  return useQuery<PaginatedTaskStateHistory>({
    queryKey: [...TASKS_KEY, "state-history", taskId, query],
    queryFn: () => getStateHistory(taskId as string, query),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation<TaskDetail, unknown, CreateTaskInput>({
    mutationFn: createTask,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation<
    TaskDetail,
    unknown,
    { taskId: string; body: UpdateTaskInput }
  >({
    mutationFn: ({ taskId, body }) => updateTask(taskId, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "detail", data.taskId] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { taskId: string }>({
    mutationFn: ({ taskId }) => deleteTask(taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
    },
  });
}

export function useWriteSnapshot() {
  const qc = useQueryClient();
  return useMutation<
    TaskSnapshot,
    unknown,
    { taskId: string; body: TaskSnapshotInput }
  >({
    mutationFn: ({ taskId, body }) => writeSnapshot(taskId, body),
    onSuccess: (data) => {
      qc.invalidateQueries({
        queryKey: [...TASKS_KEY, "snapshot", data.taskId],
      });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "detail", data.taskId] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
    },
  });
}

export function useConfigureTask() {
  const qc = useQueryClient();
  return useMutation<
    TaskSnapshot,
    unknown,
    { taskId: string; body: ConfigureRequest }
  >({
    mutationFn: ({ taskId, body }) => configureTask(taskId, body),
    onSuccess: (data) => {
      qc.invalidateQueries({
        queryKey: [...TASKS_KEY, "snapshot", data.taskId],
      });
    },
  });
}

export function useFinalizeQuote() {
  const qc = useQueryClient();
  return useMutation<
    FinalizeQuoteResponse,
    unknown,
    { taskId: string; body?: FinalizeQuoteRequest }
  >({
    mutationFn: ({ taskId, body }) => finalizeQuote(taskId, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "detail", data.taskId] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
    },
  });
}

/** CR-005: record the staff response and mark the message-only support task
 * Quoted. Invalidates the task detail + list queries so the queue reflects the
 * move from Open to Quoted. */
export function useRespondAndCloseTask() {
  const qc = useQueryClient();
  return useMutation<
    RespondAndCloseTaskResponse,
    unknown,
    { taskId: string; response: string }
  >({
    mutationFn: ({ taskId, response }) => respondAndCloseTask(taskId, response),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "detail", data.taskId] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
    },
  });
}
